import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Award, Users, Heart, CheckCircle2, ArrowRight, Star } from 'lucide-react';
import img1 from '../assets/images/jpg/img1.jpg';
import whyHireUs from '../assets/images/jpg/why-hire-us.jpg';

export default function About() {
  const variants = {
    container: { hidden: {}, show: { transition: { staggerChildren: 0.12 } } },
    fadeUp: { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } },
  };

  const cardVariant = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

  const testimonials = [
    { name: 'Sarah Johnson', role: 'Homeowner', text: 'Leafix transformed our backyard into a paradise. Truly professional and dedicated team!', img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80' },
    { name: 'Michael Chen', role: 'Business Owner', text: 'Best landscaping service in Christchurch. Our commercial space looks amazing now!', img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80' },
    { name: 'Emma Davis', role: 'Property Manager', text: 'Professional, punctual, and thorough. Highly recommend for any project size!', img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=150&q=80' },
  ];

  return (
    <div className="bg-white overflow-x-hidden">
      {/* 1. HERO SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={variants.container}
        className="relative bg-emerald-950 text-white pt-40 pb-24 px-24 overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${img1})` }}></div>
        
        <div className="relative max-w-7xl mx-auto text-center space-y-6">
          <motion.div variants={variants.fadeUp}>
            <span className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
              <Leaf size={14} className="fill-current" /> About Our Company
            </span>
          </motion.div>
          
          <motion.h1 variants={variants.fadeUp} className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.1]">
            Landscaping <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-emerald-400">
              Christchurch
            </span>
          </motion.h1>
          
          <motion.p variants={variants.fadeUp} className="text-base md:text-lg text-emerald-100/80 max-w-xl mx-auto font-normal leading-relaxed">
            We believe outdoor spaces deserve professional care and creative vision. Since 2012, we've been transforming gardens and landscapes across Christchurch with passion, expertise, and commitment to excellence.
          </motion.p>
        </div>
      </motion.section>

      {/* 2. MISSION & VALUES SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
      >
        <motion.div variants={cardVariant} className="space-y-6">
          <div>
            <span className="text-lime-600 font-extrabold uppercase tracking-wider text-xs block mb-4">Our Mission</span>
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight leading-tight mb-6">
              Transforming Outdoor Spaces with Excellence
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Our mission is to create beautiful, sustainable, and functional outdoor environments that enhance the quality of life for our clients. We combine cutting-edge design principles with eco-friendly practices to deliver landscapes that last.
            </p>
          </div>

          <div className="space-y-4">
            {[
              'Sustainable & Eco-Friendly Practices',
              'Award-Winning Design Team',
              '24/7 Customer Support',
              'Premium Quality Materials',
              'Licensed & Fully Insured',
              'Transparent Pricing Policy'
            ].map((item, i) => (
              <motion.div key={i} variants={cardVariant} className="flex items-center gap-3 text-slate-800 font-bold text-sm">
                <CheckCircle2 className="text-lime-500 flex-shrink-0" size={18} />
                <span>{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={cardVariant} className="relative">
          <div className="w-full h-[500px] bg-slate-100 rounded-[2rem] overflow-hidden shadow-xl border-8 border-white">
            <motion.img 
              src={whyHireUs} 
              alt="Our Values" 
              className="w-full h-full object-cover"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <motion.div className="absolute -bottom-6 -right-6 bg-lime-500 text-emerald-950 p-6 rounded-2xl shadow-xl border-4 border-white">
            <p className="text-3xl font-black">5+</p>
            <p className="text-xs font-black uppercase tracking-wider mt-1">Years Experience</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* 4. TEAM & EXPERTISE */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24"
      >
        <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
          <span className="text-lime-600 font-extrabold tracking-widest text-xs uppercase block">Our Team</span>
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
            Meet Our Expert Team
          </h2>
          <div className="h-1.5 w-16 bg-lime-500 mx-auto rounded-full"></div>
          <p className="text-slate-600 mt-6">
            Our experienced team of landscape architects, horticulturists, and designers work collaboratively to bring your vision to life.
          </p>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={variants.container}>
          {[
            { title: 'Design Innovation', icon: <Award />, desc: 'Creative solutions tailored to your unique space and needs.' },
            { title: 'Expert Knowledge', icon: <Leaf />, desc: 'Deep understanding of local climate, soil, and plant species.' },
            { title: 'Quality Execution', icon: <Users />, desc: 'Skilled craftspeople ensuring perfection in every detail.' }
          ].map((item, idx) => (
            <motion.div key={idx} variants={cardVariant} className="bg-slate-50 rounded-3xl p-8 border border-slate-100 hover:shadow-xl transition-all">
              <div className="bg-lime-100 text-lime-700 p-4 rounded-2xl w-fit mb-6">
                {React.cloneElement(item.icon, { size: 28 })}
              </div>
              <h3 className="font-black text-emerald-950 text-xl mb-3">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* 5. TESTIMONIALS SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="bg-stone-50 py-24 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-lime-600 font-extrabold tracking-widest text-xs uppercase block">Client Reviews</span>
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
              What Our Clients Say
            </h2>
            <div className="h-1.5 w-16 bg-lime-500 mx-auto rounded-full"></div>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={variants.container}>
            {testimonials.map((testimonial, idx) => (
              <motion.div key={idx} variants={cardVariant} className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-lime-500 text-lime-500" />
                  ))}
                </div>
                <p className="text-slate-600 leading-relaxed mb-6">"{testimonial.text}"</p>
                <div className="flex items-center gap-4 border-t border-slate-100 pt-4">
                  <img src={testimonial.img} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-black text-emerald-950 text-sm">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* 6. CTA SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24 text-center"
      >
        <motion.div variants={cardVariant} className="space-y-6">
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
            Ready to Transform Your Space?
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Contact us today for a free consultation and let's discuss how we can bring your landscaping dreams to life.
          </p>
          <motion.div variants={variants.fadeUp} className="flex flex-wrap gap-4 justify-center pt-4">
            <motion.a 
              href="tel:+64228980029"
              whileTap={{ scale: 0.98 }} 
              className="bg-lime-500 hover:bg-lime-600 text-emerald-950 px-8 py-4 rounded-xl font-extrabold text-base transition-all shadow-lg shadow-lime-500/20 inline-flex items-center gap-2"
            >
              Call Now <ArrowRight size={18} />
            </motion.a>
            <motion.a 
              href="#contact"
              whileTap={{ scale: 0.98 }} 
              className="bg-emerald-900/50 hover:bg-emerald-900 border border-emerald-800 text-white px-8 py-4 rounded-xl font-bold text-base transition-all inline-flex items-center gap-2"
            >
              Get Quote
            </motion.a>
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
}
